import * as React from "react";

import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";

import { dataHelper } from "helper/data";

const ACCENT = "#2563EB";
const BORDER = "#E4E4E7";
const TITLE = "#18181B";
const MUTED = "#71717A";
const CARD_SHADOW =
  "0px 4px 6px -2px rgba(0,0,0,0.02), 0px 10px 15px -3px rgba(0,0,0,0.04)";

const getPlainDescription = (description) => {
  if (!description) return "";
  return dataHelper.removeHTML(String(description)).trim();
};

/**
 * Blog / article card matching All-in-One Figma Multibagger_Article_Card.
 * Used by Knowledge Centre blogs, market updates, and FeaturedBlogCard.
 */
export default function BlogCard({
  title,
  imageUrl,
  description,
  category = "Investing",
  readTime = "6 min read",
  readMoreLink,
  readMoreTrack,
  onReadMore,
  sx = {},
}) {
  const plainDescription = getPlainDescription(description);

  const handleCtaClick = (event) => {
    readMoreTrack?.(event);
    if (!readMoreLink && onReadMore) {
      event.preventDefault();
      onReadMore(event);
    }
  };

  const ctaContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        cursor: "pointer",
        color: ACCENT,
      }}
    >
      <Text
        sx={{
          fontWeight: 700,
          fontSize: 16,
          color: ACCENT,
          lineHeight: "normal",
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        Read Article
      </Text>
      <ChevronRightIcon sx={{ fontSize: 16, color: ACCENT }} />
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "24px",
        boxShadow: CARD_SHADOW,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: 444 },
        width: "100%",
        ...sx,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={{
            width: "100%",
            height: 180,
            objectFit: "cover",
            flexShrink: 0,
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 180,
            bgcolor: "rgba(37,99,235,0.08)",
            flexShrink: 0,
          }}
        />
      )}

      <Stack
        sx={{
          p: "20px",
          gap: "16px",
          flex: 1,
          minHeight: { md: 264 },
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            alignSelf: "flex-start",
            px: "10px",
            py: "4px",
            borderRadius: "100px",
            border: `1px solid ${ACCENT}`,
            bgcolor: "rgba(37,99,235,0.08)",
          }}
        >
          <Text
            sx={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.44px",
              textTransform: "uppercase",
              color: ACCENT,
              lineHeight: "normal",
              fontFamily: "DM Sans, sans-serif",
            }}
          >
            {category}
          </Text>
        </Box>

        <Stack spacing="10px" sx={{ width: "100%", flex: 1 }}>
          <Text
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: 1.3,
              color: TITLE,
              fontFamily: "DM Sans, sans-serif",
            }}
          >
            {title}
          </Text>
          {plainDescription ? (
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.5,
                color: MUTED,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {plainDescription}
            </Text>
          ) : null}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", pt: "4px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: MUTED }} />
            <Text
              sx={{
                fontWeight: 500,
                fontSize: 16,
                color: MUTED,
                lineHeight: "normal",
                whiteSpace: "nowrap",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {readTime}
            </Text>
          </Box>

          {readMoreLink ? (
            <Box
              component="a"
              href={readMoreLink}
              target="_blank"
              rel="noreferrer"
              onClick={handleCtaClick}
              sx={{ textDecoration: "none" }}
            >
              {ctaContent}
            </Box>
          ) : (
            <Box onClick={handleCtaClick} role="button" tabIndex={0}>
              {ctaContent}
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
